import css from './TicketFormFields.module.scss'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import CovidAppointmentSlots from '../CovidAppointmentSlots'
import { FormFieldType } from 'meta/form'
import { ITicket } from 'meta/pages/tickets'

import { cssForm, FormField } from 'components/Forms/Form'
import { linkCountriesAndCities } from 'components/Forms/Form/FormField/LinkedDataList/utils'

import { ITicketsFormFields } from '../../types'
import { getTicketFieldsKey } from '../../utils'

interface IPropsTicketFormFields {
  formFields: ITicketsFormFields
  idx: number
  ticket: ITicket
  noPadding?: boolean
}

const TicketFormFields: React.FC<IPropsTicketFormFields> = (props) => {
  const { formFields, idx, ticket, noPadding } = props
  const fieldsKey = getTicketFieldsKey(ticket, idx)
  const { t } = useTranslation()

  const commonFields = formFields.commonFields[fieldsKey]
  const extraFields = formFields.extraFields[fieldsKey]
  const covid19Fields = formFields.covid19Fields[fieldsKey]
  const personalDataFields = formFields.personalDataFields[fieldsKey]

  const [country, setCountry] = useState<string | null>(null)

  function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, value: string) {
    const proto =
      el instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : el instanceof HTMLSelectElement
          ? HTMLSelectElement.prototype
          : HTMLInputElement.prototype

    Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value)

    const eventType = el instanceof HTMLSelectElement ? 'change' : 'input'
    el.dispatchEvent(new Event(eventType, { bubbles: true }))
  }

  const applyToAllElements = (currentElementId: string) => {
    const input = document.getElementById(currentElementId) as HTMLInputElement | HTMLSelectElement | null

    const currentTicketNumber = currentElementId[0]

    for (const key of [...Array(ticket?.groupQty ? ticket?.groupQty - 1 : 1).keys()].map(
      (i) => parseInt(currentTicketNumber) + 1 + i
    )) {
      const elem = document.getElementById(currentElementId.replace(currentTicketNumber, String(key))) as
        HTMLInputElement | HTMLSelectElement | null

      if (input && elem) {
        setNativeValue(elem, input.value)
      }
    }
  }

  return (
    <div className={classNames(cssForm.form, css.ticketFormFields, { [css.noPadding]: noPadding })}>
      {commonFields.map((field) => (
        <FormField key={field.name} formField={field} />
      ))}

      {personalDataFields.map((field) => {
        if (field.type === FormFieldType.linked) {
          field = linkCountriesAndCities(field, setCountry, country)
        }

        return <FormField key={field.name} formField={field} />
      })}

      {extraFields?.length > 0 && ticket.isPartOfGroup && (
        <div className={css.extraFields_divider}>
          <span>{t('form:extra_fields')}</span>
        </div>
      )}

      {extraFields.map((field) => {
        const required = Boolean(field.validation?.required)
        return (
          <div
            key={field.name}
            className={classNames(css.ticketFormFields__extraField, { [css.isGroupField]: ticket.isPartOfGroup })}
          >
            <div className={css.ticketFormFields__extraFieldLabel}>
              {field.fieldLabel} {!required && t('form:optional')}
            </div>

            <FormField formField={field} />

            {ticket.isFirstOfGroup && [FormFieldType.select, FormFieldType.text].includes(field.type) && (
              <div className={css.applyToAll}>
                <input
                  onClick={() => {
                    applyToAllElements(field.name)
                  }}
                  type={'checkbox'}
                ></input>
                <span>{t('form:apply_to_all_in_group')}</span>
              </div>
            )}
          </div>
        )
      })}

      {covid19Fields?.map((covid19Field) => {
        const { formField, hiddenFieldId, appointment } = covid19Field
        return (
          <div key={formField.name} className={css.ticketFormFields__extraField}>
            <div className={css.ticketFormFields__extraFieldLabel}>{appointment.name}</div>
            <div className={css.covidAppointments_wrapper}>
              <CovidAppointmentSlots
                hiddenFieldId={hiddenFieldId}
                appointmentUUID={appointment.uuid}
                slots={appointment.appointmentSlots}
              />
              <FormField formField={formField} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default TicketFormFields
